/**
 * getDefaultSelectedTechnicians
 * 
 * Determines which technicians should be pre-selected when creating or editing a visit.
 * 
 * PRECEDENCE RULES:
 * A) If editing an existing visit with technician(s) already selected -> preserve exactly
 * B) If creating a visit AND job has assigned technician(s) -> preselect those (if still assignable)
 * C) If creating a visit AND job has NO assigned techs AND job has prior visits -> 
 *    preselect technicians from the MOST RECENT visit (if still assignable)
 * D) If exactly ONE assignable technician -> auto-select that one
 * E) Otherwise -> leave empty
 * 
 * @param {Object} options
 * @param {Array<string>} options.existingVisitTechs - Technicians already assigned to the visit (for edit mode)
 * @param {Array<string>} options.jobAssignedTechs - Technicians assigned to the job
 * @param {Array<string>} options.mostRecentVisitTechs - Technicians from the job's most recent visit
 * @param {Array<Object>} options.assignableTechs - List of currently assignable technician objects
 * @param {boolean} options.isEditMode - Whether we're editing an existing visit
 * @returns {Array<string>} Array of technician IDs to pre-select
 */
export function getDefaultSelectedTechnicians({
  existingVisitTechs = [],
  jobAssignedTechs = [],
  mostRecentVisitTechs = [],
  assignableTechs = [],
  isEditMode = false,
}) {
  // Normalize IDs to strings for consistent comparison
  const normalizeId = (id) => String(id || '');
  const assignableTechIds = new Set(assignableTechs.map(t => normalizeId(t.technician_id)));
  
  // Rule A: If editing existing visit with techs, preserve them
  if (isEditMode && existingVisitTechs.length > 0) {
    return existingVisitTechs.map(normalizeId);
  }
  
  // Rule B: If job has assigned techs, preselect those that are still assignable
  if (jobAssignedTechs.length > 0) {
    const eligibleJobTechs = jobAssignedTechs
      .map(normalizeId)
      .filter(id => assignableTechIds.has(id));
    
    if (eligibleJobTechs.length > 0) {
      return eligibleJobTechs;
    }
    // If none remain after filtering, fall through to Rule C
  }
  
  // Rule C: If job has no assigned techs but has prior visits, use most recent visit's techs
  if (mostRecentVisitTechs.length > 0) {
    const eligibleRecentTechs = mostRecentVisitTechs
      .map(normalizeId)
      .filter(id => assignableTechIds.has(id));
    
    if (eligibleRecentTechs.length > 0) {
      return eligibleRecentTechs;
    }
    // If none remain after filtering, fall through to Rule D
  }
  
  // Rule D: If exactly 1 assignable tech, auto-select
  if (assignableTechs.length === 1) {
    return [normalizeId(assignableTechs[0].technician_id)];
  }
  
  // Rule D.5 (BULLETPROOF): If owner is assignable and we have no other defaults, prefer owner
  // This ensures contractors can always assign themselves without visiting Technicians tab
  const ownerTech = assignableTechs.find(t => t.is_owner);
  if (ownerTech) {
    return [normalizeId(ownerTech.technician_id)];
  }
  
  // Rule E: Leave empty
  return [];
}

/**
 * Helper to check if a technician ID is in the assignable list
 * @param {string} techId - Technician ID to check
 * @param {Array<Object>} assignableTechs - List of assignable technician objects
 * @returns {boolean}
 */
export function isTechnicianAssignable(techId, assignableTechs) {
  const normalizedId = String(techId || '');
  return assignableTechs.some(t => String(t.technician_id) === normalizedId);
}

export default getDefaultSelectedTechnicians;
